// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  RelatoriosFinanceiros.tsx
//  Relatórios de Aging (Pagar/Receber) e Inadimplência.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { BarChart3, TrendingDown, TrendingUp, AlertTriangle, RefreshCw } from 'lucide-react';
import { useAgingPagar, useAgingReceber, useInadimplencia } from '@/features/financeiro/hooks/useRelatorios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

const hoje = new Date().toISOString().slice(0, 10);

const faixaCor: Record<string, string> = {
  'A Vencer':    'bg-blue-500/10 text-blue-600',
  '0-30 dias':   'bg-yellow-500/10 text-yellow-600',
  '31-60 dias':  'bg-orange-500/10 text-orange-600',
  '61-90 dias':  'bg-red-400/10 text-red-500',
  '+90 dias':    'bg-red-600/10 text-red-700',
};

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ── Seção Aging ────────────────────────────────────────────────────────────
function AgingSection({ tipo }: { tipo: 'pagar' | 'receber' }) {
  const [dataBase, setDataBase] = useState(hoje);
  const query = tipo === 'pagar'
    ? useAgingPagar(dataBase)
    : useAgingReceber(dataBase);

  const { data, isLoading, refetch } = query;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="space-y-1">
          <Label>Data Base</Label>
          <Input type="date" value={dataBase} onChange={(e) => setDataBase(e.target.value)} className="w-40" />
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Atualizar
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : !data ? null : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Total em Aberto</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{brl(data.totalGeral)}</p>
                <p className="text-xs text-muted-foreground">{data.totalContas} conta(s)</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Data Base</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">
                  {new Date(data.dataBase).toLocaleDateString('pt-BR')}
                </p>
                <p className="text-xs text-muted-foreground">{data.faixas.length} faixa(s)</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabela */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Faixa</TableHead>
                <TableHead className="text-right">Qtd. Contas</TableHead>
                <TableHead className="text-right">Valor Total</TableHead>
                <TableHead className="text-right">% do Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.faixas.map((f) => {
                const pct = data.totalGeral > 0
                  ? ((f.valorTotal / data.totalGeral) * 100).toFixed(1)
                  : '0.0';
                return (
                  <TableRow key={f.faixa}>
                    <TableCell>
                      <Badge className={faixaCor[f.faixa] ?? 'bg-muted text-muted-foreground'}>
                        {f.faixa}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{f.qtdContas}</TableCell>
                    <TableCell className="text-right font-medium">{brl(f.valorTotal)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{pct}%</TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="font-bold border-t-2">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{data.totalContas}</TableCell>
                <TableCell className="text-right">{brl(data.totalGeral)}</TableCell>
                <TableCell className="text-right">100%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}

// ── Seção Inadimplência ────────────────────────────────────────────────────
function InadimplenciaSection() {
  const [tipo, setTipo]             = useState<'receber' | 'pagar'>('receber');
  const [diasMinimos, setDiasMin]   = useState(1);
  const [dataBase, setDataBase]     = useState(hoje);

  const { data, isLoading, refetch } = useInadimplencia(tipo, diasMinimos, dataBase);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="space-y-1">
          <Label>Tipo</Label>
          <Select value={tipo} onValueChange={(v) => setTipo(v as 'receber' | 'pagar')}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="receber">A Receber</SelectItem>
              <SelectItem value="pagar">A Pagar</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Dias mínimos de atraso</Label>
          <Input
            type="number" min={1} max={365}
            value={diasMinimos}
            onChange={(e) => setDiasMin(Number(e.target.value))}
            className="w-24"
          />
        </div>
        <div className="space-y-1">
          <Label>Data Base</Label>
          <Input type="date" value={dataBase} onChange={(e) => setDataBase(e.target.value)} className="w-40" />
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Atualizar
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" /> Total Inadimplente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-red-600">{brl(data.totalInadimplente)}</p>
                <p className="text-xs text-muted-foreground">{data.totalContas} conta(s) em {data.itens.length} {tipo === 'receber' ? 'cliente(s)' : 'fornecedor(es)'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Filtro</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">&gt; {diasMinimos} dia(s)</p>
                <p className="text-xs text-muted-foreground">base: {new Date(data.dataBase).toLocaleDateString('pt-BR')}</p>
              </CardContent>
            </Card>
          </div>

          {data.itens.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Nenhuma inadimplência encontrada para os critérios selecionados.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tipo === 'receber' ? 'Cliente' : 'Fornecedor'}</TableHead>
                  <TableHead className="text-right">Qtd. Contas</TableHead>
                  <TableHead className="text-right">Valor Total</TableHead>
                  <TableHead className="text-right">Maior Atraso</TableHead>
                  <TableHead className="text-right">Maior Conta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((item) => (
                  <TableRow key={item.entidadeId}>
                    <TableCell className="font-medium">
                      {item.nome || `${item.tipoEntidade === 'cliente' ? 'Cliente' : 'Fornecedor'} cód. ${item.entidadeId}`}
                      {item.cpfCnpj && <span className="block text-xs font-normal text-muted-foreground">{item.cpfCnpj}</span>}
                    </TableCell>
                    <TableCell className="text-right">{item.qtdContas}</TableCell>
                    <TableCell className="text-right font-medium text-red-600">
                      {brl(item.valorTotal)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge className="bg-red-500/10 text-red-600">{item.diasMaximoAtraso}d</Badge>
                    </TableCell>
                    <TableCell className="text-right">{brl(item.maiorContaVencida)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </div>
  );
}

// ── Página principal ───────────────────────────────────────────────────────
export default function RelatoriosFinanceiros() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <BarChart3 className="h-7 w-7" /> Relatórios Financeiros
        </h1>
        <p className="text-muted-foreground mt-1">
          Aging de contas a pagar/receber e relatório de inadimplência
        </p>
      </div>

      <Tabs defaultValue="aging-receber">
        <TabsList>
          <TabsTrigger value="aging-receber" className="gap-2">
            <TrendingUp className="h-4 w-4" /> Aging a Receber
          </TabsTrigger>
          <TabsTrigger value="aging-pagar" className="gap-2">
            <TrendingDown className="h-4 w-4" /> Aging a Pagar
          </TabsTrigger>
          <TabsTrigger value="inadimplencia" className="gap-2">
            <AlertTriangle className="h-4 w-4" /> Inadimplência
          </TabsTrigger>
        </TabsList>

        <TabsContent value="aging-receber" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Aging — Contas a Receber</CardTitle>
            </CardHeader>
            <CardContent>
              <AgingSection tipo="receber" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aging-pagar" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Aging — Contas a Pagar</CardTitle>
            </CardHeader>
            <CardContent>
              <AgingSection tipo="pagar" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inadimplencia" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Inadimplência por {'{'}cliente / fornecedor{'}'}</CardTitle>
            </CardHeader>
            <CardContent>
              <InadimplenciaSection />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
